import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { DISABLE_MANUAL_DEPENDENCIES_REVAMP } from '../../../../../shared/featureFlags/flagNames';
import { isProjectScope } from '../../../../../shared/issuesV2';
import { useDresser } from '../../../../contexts/DrawerContext';
import { useParams } from '../../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import AddDependencyDrawer from '../../../drawers/AddDependencyDrawer';
import { Remediation } from '../../../shared/vulns/Drawer';
import type { VulnIssue } from '../../../../../shared/issuesV2';
import type { RevisionRouteParams } from '../../../projects/projectDashboardTypes';

/**
 * VulnerabilityIssueDetailsRemediation displays the remediation info
 * of the vulnerability currently being viewed.
 */
const VulnerabilityIssueDetailsRemediation: VoidFunctionComponent = () => {
  const activeIssue = useSelector(IssuesV2Store.selectors.getIssueDetails) as VulnIssue;
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const params = useParams<RevisionRouteParams>();
  const { pushDrawer, popDrawer, clearDrawers } = useDresser();
  const isManualDependenciesRevampEnabled = !useIsFeatureEnabled(DISABLE_MANUAL_DEPENDENCIES_REVAMP);

  if (!activeIssue || activeIssue.source.packageManager === 'csbinary') {
    return null;
  }

  /**
   * User-defined dependencies can have vuln issues due to CPEs in the SBOM file.
   * However, they don't have a remediation guidance, but we still want to show an unable to calculate message.
   */
  const isUserDefinedDependency = activeIssue.source?.packageManager === 'user';
  //  Pair the CPE check with the `user` fetcher so a third-party dep that merely carries a
  //  CPE in its SBOM metadata doesn't get the CPE treatment.
  const isDetectedViaCpe = isUserDefinedDependency && 'cpes' in activeIssue && !!activeIssue.cpes?.length;

  //  `remediation` only exists on vulnerability issues (malware issues have none), so narrow on the
  //  discriminant rather than relying on control-flow inference for the unconditional access below.
  const remediation = activeIssue.type === 'vulnerability' ? activeIssue.remediation : undefined;

  //  Guard on both id fields, not just the params object: a present-but-partial params (e.g. a
  //  missing revision_id) would otherwise build a truthy `undefined$undefined` locator and enable
  //  the overwrite with an invalid revision.
  const userRevisionLocator = params?.project_id && params.revision_id
    ? `${params.project_id}$${params.revision_id}`
    : '';
  const canOverwrite = isManualDependenciesRevampEnabled
    && isProjectScope(scope)
    && isDetectedViaCpe
    && !!userRevisionLocator;

  //  Stack the overwrite drawer on top of the issue drawer: back returns to the issue,
  //  a successful save closes the whole stack.
  const onUpdateDependency = canOverwrite
    ? () => pushDrawer(
      <AddDependencyDrawer
        userRevisionLocator={userRevisionLocator}
        overwriteDependencyLocator={activeIssue.source.id}
        onClose={popDrawer}
        onSaved={clearDrawers}
      />,
      { onBack: popDrawer, onClose: clearDrawers, shouldSkipClickOutside: true },
      )
    : undefined;

  return (
    <Remediation
      version={activeIssue.source?.version ?? ''}
      //  Note: we need to expand the API response to include both.
      completeFix={remediation?.completeFix}
      completeFixDistance={remediation?.completeFixDistance}
      partialFix={remediation?.partialFix}
      isUserDefinedDependency={isUserDefinedDependency}
      isDetectedViaCpe={isDetectedViaCpe}
      onUpdateDependency={onUpdateDependency}
    />
  );
};

export default VulnerabilityIssueDetailsRemediation;
